
import {
  Control,
  FieldValues,
  FieldPath,
  UseFormGetValues,
  UseFormSetValue,
  ControllerFieldState,
  UseFormStateReturn,
  Controller,
} from 'react-hook-form';
import { ReactNode, useRef, useMemo } from 'react';
import { useWatch } from 'react-hook-form';

// 渲染参数（对标 Controller）
interface ArrayFieldRenderProps<
  TFieldValues extends FieldValues = FieldValues,
  TInternalValue = any
> {
  field: {
    value: (TInternalValue | null)[];
    onChange: (value: (TInternalValue | null)[] | null) => void;
    onBlur: () => void;
    ref: React.Ref<any>;
    name?: string; // 可选，用于调试
  };
  fieldState: Partial<ControllerFieldState>; // 简化版，可扩展
  formState: Partial<UseFormStateReturn<TFieldValues>>; // 若需要完整 formState，需额外传入
}

interface ArrayFieldControllerProps<
  TFieldValues extends FieldValues = FieldValues,
  TInternalValue = any
> {
  names: readonly FieldPath<TFieldValues>[];
  control: Control<TFieldValues>;
  setValue?: UseFormSetValue<TFieldValues>;   // 可选，若未提供则从 control 拿（但官方不暴露）
  getValues?: UseFormGetValues<TFieldValues>; // 同上
  formatValue?: (value: TInternalValue) => any;
  parseValue?: (formatted: any) => TInternalValue;
  shouldValidateOnChange?: boolean;
  render: (props: ArrayFieldRenderProps<TFieldValues, TInternalValue>) => ReactNode;
}

export function ArrayFieldController<
  TFieldValues extends FieldValues = FieldValues,
  TInternalValue = any
>({
  names,
  control,
  setValue: externalSetValue,
  getValues: externalGetValues,
  formatValue = (v) => v,
  parseValue = (v) => v,
  shouldValidateOnChange = true,
  render,
}: ArrayFieldControllerProps<TFieldValues, TInternalValue>) {
  // ⚠️ 注意：control 不直接暴露 setValue/getValues
  // 所以必须由调用方传入（这是唯一安全方式）
  if (!externalSetValue || !externalGetValues) {
    throw new Error(
      'ArrayFieldController requires `setValue` and `getValues` to be passed explicitly. ' +
      'Example: <ArrayFieldController control={control} setValue={setValue} getValues={getValues} ... />'
    );
  }

  const { setValue, getValues } = { setValue: externalSetValue, getValues: externalGetValues };
  const fieldRef = useRef<any>(null);

  const fieldValues = useWatch({
// components/ArrayFieldController.tsx
    control: control as any,
    name: names as any,
    defaultValue: names.map(() => null),
  });

  const internalValue = useMemo(() => {
    return fieldValues.map((val: any) => (val != null ? parseValue(val) : null));
  }, [fieldValues, parseValue]);

  const handleChange = (newInternalValues: (TInternalValue | null)[] | null) => {
    if (!Array.isArray(newInternalValues)) {
      newInternalValues = names.map(() => null);
    }

    const safeNewValues = [...newInternalValues];
    while (safeNewValues.length < names.length) {
      safeNewValues.push(null);
    }

    const formattedValues = safeNewValues.map((val) =>
      val != null ? formatValue(val) : null
    );

    names.forEach((name, index) => {
      const newVal = formattedValues[index];
      const oldVal = fieldValues[index];
      if (newVal !== oldVal) {
        setValue(name, newVal as any, {
          shouldValidate: shouldValidateOnChange,
          shouldDirty: true,
          shouldTouch: false,
        });
      }
    });
  };

  const handleBlur = () => {
    // names.forEach((name) => {
    //   const currentValue = getValues(name);
    //   setValue(name, currentValue, {
    //     shouldValidate: false,
    //     shouldDirty: false,
    //     shouldTouch: true,
    //   });
    // });
  };

  const field = {
    value: internalValue,
    onChange: handleChange,
    onBlur: handleBlur,
    ref: fieldRef,
    name: names.join(','), // 调试用
  };

  // 简化的 fieldState / formState（如需完整版，建议传入 formState）
  const fieldState: Partial<ControllerFieldState> = {};
  const formState: Partial<UseFormStateReturn<TFieldValues>> = {};

  return (
    <>
      {names.map((name) => (
        <Controller
          key={name}
          name={name}
          control={control}
          render={() => <></>}
        />
      ))}
      {render({ field, fieldState, formState })}
    </>
  );
}
